import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import type { RadioChangeEvent } from 'antd'
import { observer } from 'mobx-react'

import {
  Cartesian3,
  Cartographic,
  Math as CesiumMath,
  ClassificationType,
  CustomDataSource,
  HeadingPitchRoll,
  ScreenSpaceEventHandler,
  ScreenSpaceEventType,
  Transforms,
} from '@cesiumgs/cesium-analytics'
import { Button } from 'antd'
import {
  AIR,
  DIRECTION,
  DISTANCE,
  FLOOR,
  HORIZONTAL,
  MODEL_GLB,
  ROLL,
  VERTICAL,
  curParam,
  directionConfig,
  distanceConfig,
  horizontalConfig,
  rollConfig,
  verticalConfig,
} from './data'
import SliderComponent from './sliderItem'
import VisualAnalysis from '@/components/Cesium/VisualAnalysis'
import { Radio, RadioGroup } from '@/components/UiComponent'
import useStore from '@/hooks/useStore'

import './index.less'

const prefix = 'visualAreaAnalysis'

let visualAnalysisArr: Array<VisualAnalysis> = []
let onMapClickHandlerInstance: any = null
let onMapMouseMoveHandlerInstance: any = null

const VisualAreaAnalysis = forwardRef((props: any, ref) => {
  const rightNavCtrlStore = useStore('rightNavCtrlStore')
  // const { visibleCopy, handleCloseModal } = props
  const [radioValue, setRadioValue] = useState(FLOOR)
  const [direction, setDirection] = useState(curParam.direction)
  const [roll, setRoll] = useState(curParam.roll)
  const [distance, setDistance] = useState(curParam.distance)
  const [horizontal, setHorizontal] = useState(curParam.horizontal)
  const [vertical, setVertical] = useState(curParam.vertical)
  const [collect, setCollect] = useState(new CustomDataSource(`${prefix}-entityCollection`))
  const [peopleCartesian3, setPeopleCartesian3] = useState<Cartesian3 | null>(null)
  const [disabled, setDisabled] = useState(true)

  const modelRef = useRef<string>()
  modelRef.current = radioValue

  useImperativeHandle(ref, () => ({
    handleClearAll,
  }))

  useEffect(() => {
    return () => {
      handleClearAll()
    }
  }, [])

  const init = () => {
    window.cviewer.dataSources.add(collect)
    onMapClickHandlerInstance = new ScreenSpaceEventHandler(window.cviewer.canvas)
    onMapMouseMoveHandlerInstance = new ScreenSpaceEventHandler(window.cviewer.canvas)
    onMapClickHandlerInstance.setInputAction(onMouseClickHandler, ScreenSpaceEventType.LEFT_CLICK)

    return () => {
      window.cviewer.dataSources.remove(collect)
      onMapClickHandlerInstance.removeInputAction(ScreenSpaceEventType.LEFT_CLICK)
      onMapMouseMoveHandlerInstance.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE)
      onClear()
    }
  }

  const onClear = () => {
    collect.entities.removeAll()
    if (visualAnalysisArr.length > 0) {
      visualAnalysisArr[0].clear()
      visualAnalysisArr = []
    }
  }

  const handleDrawPoint = (cartesian3: Cartesian3, directionpram: any) => {
    onClear()
    const newcartesian3 = cartesian3 || peopleCartesian3
    const newdirection = directionpram || direction
    const heading = CesiumMath.toRadians(newdirection + 90)
    const pitch = 0
    const roll = 0
    const hpr = new HeadingPitchRoll(heading, pitch, roll)
    const orientation: any = Transforms.headingPitchRollQuaternion(newcartesian3, hpr)
    collect.entities.add({
      id: MODEL_GLB,
      position: newcartesian3,
      orientation,
    })
  }

  const onMouseClickHandler = (evt: any) => {
    let position
    if (modelRef.current === FLOOR) {
      const ray = window.cviewer.camera.getPickRay(evt.position)
      position = window.cviewer.scene.globe.pick(ray, window.cviewer.scene)
    }
    else {
      position = window.cviewer.scene.pickPosition(evt.position)
    }
    const cartographic = Cartographic.fromCartesian(position)
    const lon = CesiumMath.toDegrees(cartographic.longitude)
    const lat = CesiumMath.toDegrees(cartographic.latitude)
    const altpeople = cartographic.height // cartographic.height + 1.8;
    const altnewPos = cartographic.height + 1.8
    const newPos = Cartesian3.fromDegrees(lon, lat, altnewPos)
    const newpeoplePos: any = Cartesian3.fromDegrees(lon, lat, altpeople)
    setPeopleCartesian3(newpeoplePos)
    handleDrawPoint(newpeoplePos, direction)
    setDirection(curParam.direction)
    setHorizontal(curParam.horizontal)
    setVertical(curParam.vertical)
    setRoll(curParam.roll)
    setDistance(curParam.distance)
    const id = `${collect.entities.values.length}analysis`
    const visualAnalysis = new VisualAnalysis({
      id,
      basePoint: newPos,
      view: window.cviewer,
      xHalfAngle: curParam.horizontal,
      yHalfAngle: curParam.vertical,
      heading: curParam.direction,
      pitch: 0,
      roll: curParam.roll,
      classificationType: ClassificationType.BOTH,
      radius: curParam.distance,
      billbordPos: null,
    } as any)
    setDisabled(false)
    visualAnalysisArr.push(visualAnalysis)
  }

  const radioChange = (e: RadioChangeEvent) => {
    setRadioValue(e.target.value)
  }

  const movePoint = (cartesian3: Cartesian3 | null, directionpram: any) => {
    const models: any = collect.entities.getById(MODEL_GLB)
    const newcartesian3: any = cartesian3 || peopleCartesian3
    const newdirection = directionpram || direction
    const heading = CesiumMath.toRadians(newdirection + 90)
    const pitch = 0
    const roll = 0
    const hpr = new HeadingPitchRoll(heading, pitch, roll)
    const orientation = Transforms.headingPitchRollQuaternion(newcartesian3, hpr)
    models.orientation = orientation
  }

  const handleSure = () => {
    init()
  }
  const handleClearAll = () => {
    onClear()
    onMapClickHandlerInstance?.removeInputAction(ScreenSpaceEventType.LEFT_CLICK)
    onMapMouseMoveHandlerInstance?.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE)
  }

  const onDirectionChange = (value: number | null) => {
    if (visualAnalysisArr.length > 0) {
      visualAnalysisArr[visualAnalysisArr.length - 1].setHeadingPitchRoll(
        value || 0,
        0,
        roll,
      )
      setDirection(value || 0)
      movePoint(peopleCartesian3, value)
    }
  }

  const onRollChange = (value: number | null) => {
    if (visualAnalysisArr.length > 0) {
      visualAnalysisArr[visualAnalysisArr.length - 1].setHeadingPitchRoll(
        direction,
        0,
        value || 0,
      )
      setRoll(value || 0)
    }
  }

  const onDistanceChange = (value: number | null) => {
    if (visualAnalysisArr.length > 0) {
      visualAnalysisArr[visualAnalysisArr.length - 1].setDistance(value || 1)
      setDistance(value || 1)
    }
  }

  const onHorizontalChange = (value: number | null) => {
    if (visualAnalysisArr.length > 0) {
      visualAnalysisArr[visualAnalysisArr.length - 1].setHorizontal(value || 1)
      setHorizontal(value || 1)
    }
  }
  const onVerticalChange = (value: number | null) => {
    if (visualAnalysisArr.length > 0) {
      visualAnalysisArr[visualAnalysisArr.length - 1].setVertical(value || 1)
      setVertical(value || 1)
    }
  }

  const valueChangeCallback = (name: string, value: number | null) => {
    switch (name) {
      case DIRECTION:
        onDirectionChange(value)
        break
      case ROLL:
        onRollChange(value)
        break
      case DISTANCE:
        onDistanceChange(value)
        break
      case HORIZONTAL:
        onHorizontalChange(value)
        break
      case VERTICAL:
        onVerticalChange(value)
        break
      default:
        break
    }
  }

  return (
      <div className={`${prefix}`}>
        <div className={`${prefix}-box1`}>
          <div className={`${prefix}-box1-text`}>
            <span>시점 지정</span>
            <span>* 시점을 지도에서 선택하세요</span>
          </div>
          <div className={`${prefix}-box1-btn`}>
            <Button className={`${prefix}-box1-btn-sure`} onClick={handleSure}>시점 지정</Button>
            <Button disabled={disabled} className={`${prefix}-box1-btn-cancel`} onClick={handleClearAll}>지정 취소</Button>
          </div>
        </div>
        <div className={`${prefix}-box2`}>
          <p>모드</p>
          <RadioGroup onChange={radioChange} value={radioValue}>
            <Radio value={FLOOR}>지면 모드</Radio>
            <Radio value={AIR}>공간 모드</Radio>
          </RadioGroup>
        </div>
        <div className={`${prefix}-box3`}>
          <div className={`${prefix}-box3-one`}>
            <p>수평 방향각</p>
            <SliderComponent valueChangeCallback={(name: string, value: number | null) => valueChangeCallback(name, value)} sliderConfig={directionConfig} name={DIRECTION} value={curParam.direction}></SliderComponent>
          </div>
          <div className={`${prefix}-box3-one`}>
            <p>수직 방향각</p>
            <SliderComponent valueChangeCallback={(name: string, value: number | null) => valueChangeCallback(name, value)} sliderConfig={rollConfig} name={ROLL} value={curParam.roll}></SliderComponent>
          </div>
          <div className={`${prefix}-box3-one`}>
            <p>가시거리</p>
            <SliderComponent valueChangeCallback={(name: string, value: number | null) => valueChangeCallback(name, value)} sliderConfig={distanceConfig} name={DISTANCE} value={curParam.distance}></SliderComponent>
          </div>
          <div className={`${prefix}-box3-one`}>
            <p>수평 시야각</p>
            <SliderComponent valueChangeCallback={(name: string, value: number | null) => valueChangeCallback(name, value)} sliderConfig={horizontalConfig} name={HORIZONTAL} value={curParam.horizontal}></SliderComponent>
          </div>
          <div className={`${prefix}-box3-one`}>
            <p>수직 시야각</p>
            <SliderComponent valueChangeCallback={(name: string, value: number | null) => valueChangeCallback(name, value)} sliderConfig={verticalConfig} name={VERTICAL} value={curParam.vertical}></SliderComponent>
          </div>
        </div>
      </div>
  )
})

export default observer(VisualAreaAnalysis)
